import { useCallback, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { alpha, styled } from "@material-ui/core/styles";
import { keyframes } from "@emotion/react";
import { LoadingButton } from "@material-ui/lab";
import AutoAwesomeIcon from "@material-ui/icons/AutoAwesome";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import CloseIcon from "@material-ui/icons/Close";
import FacebookIcon from "@material-ui/icons/Facebook";
import FavoriteIcon from "@material-ui/icons/Favorite";
import GroupsIcon from "@material-ui/icons/Groups";
import InsightsIcon from "@material-ui/icons/Insights";
import PublicIcon from "@material-ui/icons/Public";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ThumbUpIcon from "@material-ui/icons/ThumbUp";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import useHasSubmittedFeedback from "hooks/useHasSubmittedFeedback";
import usePromoBanner, { PROMO_BANNER_IDS } from "hooks/usePromoBanner";
import { PATH_DASHBOARD } from "routes/paths";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import {
  UserFeedback,
  USER_FEEDBACKS_COLLECTION_NAME
} from "../../../@types/sharedSchema";

/**
 * "Facebook group posting" beta launch: the home page promo AND the pieces the feature
 * update's popup embeds (`<fbpostinghero>` and `<fbpostingbeta>` in FeatureUpdateArticle), so
 * the announcement looks and behaves the same in both places.
 *
 * The feature (/dashboard/fb-posting) is opened per account by us (`facebookGroupPosting`), and
 * it is a paid add-on: the banner says so in small print. The CTA is the beta sign-up, a
 * `user-feedbacks` doc tagged FB_POSTING_BETA_FEEDBACK_TAG (the record of the signup, read back by
 * useHasSubmittedFeedback). An account that already holds the feature is sent to the page instead.
 *
 * Self-gating like the other promos: parents mount it unconditionally.
 */

// Master kill switch — flip to false to hide the home page banner everywhere immediately.
const PROMO_ENABLED = true;

// featureTag on the `user-feedbacks` doc: groups the signups in the admin "משובי משתמשים" page and
// is what useHasSubmittedFeedback matches on. Never rename it: it is the record of who signed up.
export const FB_POSTING_BETA_FEEDBACK_TAG = "facebook-group-posting-beta";

// Facebook's blue and the deep navy the gradient fades from — not in the theme palette, so pinned here.
const FB_BLUE = "#1877F2";
const FB_NAVY = "#0B2A8A";
// FB blue on WHITE (the CTA's label, the dialog's buttons): the brand blue itself is too light for text.
const FB_BLUE_INK = "#0B5CD5";

const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7); }
  70% { box-shadow: 0 0 0 7px rgba(74, 222, 128, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
`;

/** The launch's background: navy into Facebook blue, a violet and a cyan glow, a fading dot grid. */
const gradientSurface = {
  position: "relative",
  overflow: "hidden",
  color: "#fff",
  backgroundColor: FB_NAVY,
  backgroundImage: [
    "radial-gradient(60% 90% at 12% 18%, rgba(147, 97, 255, 0.55) 0%, rgba(147, 97, 255, 0) 70%)",
    "radial-gradient(55% 80% at 92% 105%, rgba(34, 211, 238, 0.32) 0%, rgba(34, 211, 238, 0) 70%)",
    `linear-gradient(135deg, ${FB_NAVY} 0%, #1450D8 48%, ${FB_BLUE} 100%)`
  ].join(", "),
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    backgroundImage: "radial-gradient(rgba(255, 255, 255, 0.16) 1px, transparent 1px)",
    backgroundSize: "18px 18px",
    maskImage: "linear-gradient(115deg, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 0.9) 100%)",
    WebkitMaskImage: "linear-gradient(115deg, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 0.9) 100%)"
  }
} as const;

const RootStyle = styled(Card)(() => ({
  ...gradientSurface,
  boxShadow: `0 18px 40px -18px ${alpha(FB_BLUE, 0.65)}`,
  border: `1px solid ${alpha("#fff", 0.12)}`
}));

// ---- beta sign-up state ----------------------------------------------------------------------

// Who signed up during this page load. The banner and the popup's button each run their own
// feedback lookup, so a signup in one must reach the other without a re-query. Keyed by email so a
// sign-out and sign-in as someone else in the same tab does not inherit it.
const joinedThisPage = new Set<string>();
const joinListeners = new Set<() => void>();

function useFbPostingBetaSignup(): { hasJoined: boolean; markJoined: () => void } {
  const { user } = useAuth();
  const email = user?.email ?? "";
  const { hasSubmitted, markSubmitted } = useHasSubmittedFeedback(FB_POSTING_BETA_FEEDBACK_TAG);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    joinListeners.add(rerender);
    return () => {
      joinListeners.delete(rerender);
    };
  }, []);

  const markJoined = useCallback(() => {
    if (email) joinedThisPage.add(email);
    markSubmitted();
    joinListeners.forEach((listener) => listener());
  }, [email, markSubmitted]);

  return { hasJoined: hasSubmitted || (!!email && joinedThisPage.has(email)), markJoined };
}

/** The account already holds the feature: it is past the beta queue, so it is sent to the page. */
function useHasFbPosting(): boolean {
  const { userSettings } = useFirestore();
  return userSettings?.advancedFeatures?.includes("facebookGroupPosting") ?? false;
}

// ---- the artwork -----------------------------------------------------------------------------

// The mock post's photos: Webot's own makeover demo rooms (public/static/makeovers), resized —
// never a scraped listing's photo. A new picture gets a new file name (Cloudflare keeps /static/**).
const LISTING_PHOTOS = [
  "/static/_haim/fb-posting-promo-listing-1.jpg",
  "/static/_haim/fb-posting-promo-listing-2.jpg",
  "/static/_haim/fb-posting-promo-listing-3.jpg"
] as const;

/** One photo of the mock post's grid, filling its cell. */
function GridPhoto({ src, sx }: { src: string; sx?: Record<string, unknown> }): JSX.Element {
  return (
    <Box
      component="img"
      src={src}
      alt=""
      loading="lazy"
      sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover", minWidth: 0, minHeight: 0, ...sx }}
    />
  );
}

/** The listing's photos as Facebook lays out a post with several: one large, two small, "+4" on the last. */
function ListingPhotos(): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gridTemplateRows: "1fr 1fr",
        gap: "2px",
        height: 112,
        backgroundColor: "#fff"
      }}
    >
      <GridPhoto src={LISTING_PHOTOS[0]} sx={{ gridRow: "1 / span 2" }} />
      <GridPhoto src={LISTING_PHOTOS[1]} />
      <Box component="span" sx={{ position: "relative", display: "block", minHeight: 0 }}>
        <GridPhoto src={LISTING_PHOTOS[2]} />
        <Box
          component="span"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: 700,
            color: "#fff",
            backgroundColor: "rgba(0, 0, 0, 0.42)"
          }}
          // A leading "+" is bidi-neutral: in a Hebrew page it reads "4+". The attribute, not a CSS
          // `direction`, which the RTL style plugin flips.
          dir="ltr"
        >
          +4
        </Box>
      </Box>
    </Box>
  );
}

/** A placeholder line of text on a mock post. */
function SkeletonLine({ width, light = false }: { width: string; light?: boolean }): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        display: "block",
        height: 7,
        width,
        borderRadius: 4,
        backgroundColor: light ? alpha("#fff", 0.35) : "#E4E6EB"
      }}
    />
  );
}

/** A floating status pill around the mock post. */
function FloatingPill({
  icon,
  label,
  tone,
  delay,
  sx
}: {
  icon: JSX.Element;
  label: string;
  tone: "success" | "glass" | "ai";
  delay: string;
  sx: Record<string, unknown>;
}): JSX.Element {
  const palette = {
    success: { bg: "#fff", fg: "#15803D" },
    glass: { bg: alpha("#0B1640", 0.55), fg: "#fff" },
    ai: { bg: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)", fg: "#fff" }
  }[tone];
  return (
    <Box
      component="span"
      sx={{
        position: "absolute",
        zIndex: 3,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.25,
        py: 0.6,
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        color: palette.fg,
        background: palette.bg,
        border: tone === "glass" ? `1px solid ${alpha("#fff", 0.25)}` : "none",
        backdropFilter: tone === "glass" ? "blur(8px)" : undefined,
        boxShadow: "0 10px 24px -8px rgba(5, 15, 60, 0.55)",
        animation: `${floatY} 6s ease-in-out ${delay} infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        "& svg": { fontSize: 15 },
        ...sx
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

/**
 * The launch artwork: a Facebook group post of a listing, two more behind it, and the status the
 * feature gives it (posted to many groups, the next post scheduled, written with AI). Built of
 * spans so it can sit inside a Markdown paragraph in the popup (see LocationGroupsCtaButton).
 */
export function FbPostingPromoArt(): JSX.Element {
  const { t } = useTranslation();
  const card = {
    position: "absolute",
    width: 250,
    borderRadius: "14px",
    overflow: "hidden"
  } as const;

  return (
    <Box
      component="span"
      aria-hidden
      sx={{ position: "relative", display: "block", width: 330, height: 308, flexShrink: 0 }}
    >
      {/* The posts behind: the same listing, already up in other groups. */}
      <Box
        component="span"
        sx={{
          ...card,
          top: 30,
          insetInlineStart: 18,
          height: 220,
          transform: "rotate(-9deg)",
          backgroundColor: alpha("#fff", 0.14),
          border: `1px solid ${alpha("#fff", 0.22)}`
        }}
      />
      <Box
        component="span"
        sx={{
          ...card,
          top: 14,
          insetInlineStart: 52,
          height: 236,
          transform: "rotate(6deg)",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          backgroundColor: alpha("#fff", 0.22),
          border: `1px solid ${alpha("#fff", 0.3)}`,
          backdropFilter: "blur(4px)"
        }}
      >
        <SkeletonLine width="55%" light />
        <SkeletonLine width="80%" light />
      </Box>

      {/* The post in front. */}
      <Box
        component="span"
        sx={{
          ...card,
          top: 22,
          insetInlineStart: 38,
          zIndex: 2,
          display: "block",
          backgroundColor: "#fff",
          color: "#050505",
          boxShadow: "0 24px 48px -16px rgba(3, 10, 48, 0.6)",
          textAlign: "start"
        }}
      >
        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, pt: 1.25 }}>
          <Box
            component="span"
            sx={{
              width: 30,
              height: 30,
              borderRadius: "9px",
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${FB_BLUE} 0%, #6D5BFF 100%)`,
              color: "#fff"
            }}
          >
            <GroupsIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box component="span" sx={{ display: "block", minWidth: 0 }}>
            <Box component="span" sx={{ display: "block", fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>
              {t("dashboardPage.widget.fbPostingPromo.art.groupName")}
            </Box>
            <Box
              component="span"
              sx={{ display: "flex", alignItems: "center", gap: 0.4, fontSize: 10.5, color: "#65676B" }}
            >
              {t("dashboardPage.widget.fbPostingPromo.art.postMeta")}
              <PublicIcon sx={{ fontSize: 11 }} />
            </Box>
          </Box>
        </Box>
        <Box
          component="span"
          sx={{ display: "block", px: 1.5, pt: 1, pb: 0.9, fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
        >
          {t("dashboardPage.widget.fbPostingPromo.art.postText")}
        </Box>
        <Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.6, px: 1.5, pb: 1.1 }}>
          <SkeletonLine width="88%" />
          <SkeletonLine width="62%" />
        </Box>
        <ListingPhotos />
        <Box
          component="span"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 0.9,
            fontSize: 10.5,
            color: "#65676B"
          }}
        >
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Box component="span" sx={{ display: "inline-flex" }}>
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: FB_BLUE,
                  border: "1.5px solid #fff"
                }}
              >
                <ThumbUpIcon sx={{ fontSize: 9, color: "#fff" }} />
              </Box>
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  marginInlineStart: "-4px",
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#F33E58",
                  border: "1.5px solid #fff"
                }}
              >
                <FavoriteIcon sx={{ fontSize: 9, color: "#fff" }} />
              </Box>
            </Box>
            48
          </Box>
          {t("dashboardPage.widget.fbPostingPromo.art.comments")}
        </Box>
      </Box>

      <FloatingPill
        tone="success"
        delay="0s"
        icon={<CheckCircleRoundedIcon />}
        label={t("dashboardPage.widget.fbPostingPromo.art.postedPill")}
        sx={{ top: 4, insetInlineEnd: 0 }}
      />
      <FloatingPill
        tone="ai"
        delay="1.2s"
        icon={<AutoAwesomeIcon />}
        label={t("dashboardPage.widget.fbPostingPromo.art.aiPill")}
        sx={{ top: 118, insetInlineStart: 0 }}
      />
      <FloatingPill
        tone="glass"
        delay="2.4s"
        icon={<ScheduleIcon />}
        label={t("dashboardPage.widget.fbPostingPromo.art.nextPill")}
        sx={{ bottom: 0, insetInlineEnd: 14 }}
      />
    </Box>
  );
}

/** The popup's hero (`<fbpostinghero>`): the artwork on the launch gradient. */
export function FbPostingPromoHero(): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        ...gradientSurface,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "14px",
        mt: 0,
        mb: 2,
        py: { xs: 2.5, sm: 3 },
        "& > span": { transform: { xs: "scale(0.86)", sm: "none" } }
      }}
    >
      <FbPostingPromoArt />
    </Box>
  );
}

// ---- the sign-up -----------------------------------------------------------------------------

type SignupSource = "dashboard-promo-banner" | "feature-update-popup";

/**
 * The beta sign-up. Name and phone are read off the profile (never retyped); the one optional
 * question is where they post today, which is what the team needs to onboard them.
 */
function FbPostingBetaDialog({
  open,
  source,
  onClose,
  onJoined
}: {
  open: boolean;
  source: SignupSource;
  onClose: () => void;
  onJoined: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { userSettings } = useFirestore();

  const [postingToday, setPostingToday] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset once per open, so a re-open starts on the form, not on the previous thank-you.
  useEffect(() => {
    if (!open) return;
    setPostingToday("");
    setSubmitted(false);
    setError(null);
  }, [open]);

  // The real source for the phone is userSettings.ownerPhoneNumber (set at onboarding);
  // user.phoneNumber is only there for phone-auth sign-ins, i.e. almost nobody.
  const contactName = userSettings?.ownerFullName || user?.displayName || "";
  const phoneRaw = userSettings?.ownerPhoneNumber || user?.phoneNumber || "";
  const contactPhone = parsePhoneNumber(phoneRaw)?.formatNational() || phoneRaw;

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (!user?.email) throw new Error("Missing user email");
      const answer = postingToday.trim();

      // The admin feedback card renders `description` only, so everything the team needs to act
      // goes there as readable lines; `metadata` keeps the contact details structured too.
      const description = [
        t("dashboardPage.widget.fbPostingPromo.feedback.registered"),
        `${t("dashboardPage.widget.fbPostingPromo.dialog.name")}: ${contactName || "—"}`,
        `${t("dashboardPage.widget.fbPostingPromo.dialog.phone")}: ${contactPhone || "—"}`,
        ...(answer ? ["", t("dashboardPage.widget.fbPostingPromo.dialog.postingTodayLabel"), answer] : [])
      ].join("\n");

      const now = new Date();
      const payload: UserFeedback = {
        userId: user.email,
        description,
        featureTag: FB_POSTING_BETA_FEEDBACK_TAG,
        metadata: {
          locale: i18n.language,
          source,
          ...(contactName ? { contactName } : {}),
          ...(contactPhone ? { contactPhone } : {})
        },
        createdTime: now,
        updatedTime: now
      };
      await db.collection(USER_FEEDBACKS_COLLECTION_NAME).doc().set(payload);

      setSubmitted(true);
      onJoined();
    } catch (e) {
      console.error("Failed to sign up for the Facebook group posting beta", e);
      setError(t("dashboardPage.widget.fbPostingPromo.dialog.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Stack direction="row" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${FB_NAVY} 0%, ${FB_BLUE} 100%)`
              }}
            >
              <FacebookIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Typography variant="h5">
              {submitted
                ? t("dashboardPage.widget.fbPostingPromo.dialog.successTitle")
                : t("dashboardPage.widget.fbPostingPromo.dialog.title")}
            </Typography>
          </Stack>
          <IconButton onClick={handleClose} disabled={submitting} aria-label={t("general.close") as string}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {submitted ? (
          <Stack alignItems="center" gap={1.5} sx={{ py: 3, textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
            <Typography variant="body1" sx={{ maxWidth: 420 }}>
              {t("dashboardPage.widget.fbPostingPromo.dialog.successBody")}
            </Typography>
          </Stack>
        ) : (
          <Stack gap={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t("dashboardPage.widget.fbPostingPromo.dialog.subtitle")}
            </Typography>

            {(contactName || contactPhone) && (
              <Box
                sx={{
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 1.5,
                  backgroundColor: (theme) => alpha(FB_BLUE, theme.palette.mode === "dark" ? 0.16 : 0.07)
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {t("dashboardPage.widget.fbPostingPromo.dialog.contactLabel")}
                </Typography>
                <Typography variant="subtitle2">
                  {[contactName, contactPhone].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                {t("dashboardPage.widget.fbPostingPromo.dialog.postingTodayLabel")}{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  {t("dashboardPage.widget.fbPostingPromo.dialog.optional")}
                </Typography>
              </Typography>
              <TextField
                value={postingToday}
                onChange={(e) => setPostingToday(e.target.value)}
                placeholder={t("dashboardPage.widget.fbPostingPromo.dialog.postingTodayPlaceholder")}
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                disabled={submitting}
              />
            </Box>

            <Typography variant="caption" color="text.secondary">
              {t("dashboardPage.widget.fbPostingPromo.paidNote")}
            </Typography>

            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {submitted ? (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ bgcolor: FB_BLUE_INK, "&:hover": { bgcolor: "#0A4DB3" } }}
          >
            {t("dashboardPage.widget.fbPostingPromo.dialog.done")}
          </Button>
        ) : (
          <>
            <Button color="inherit" onClick={handleClose} disabled={submitting}>
              {t("general.close")}
            </Button>
            <LoadingButton
              loading={submitting}
              variant="contained"
              onClick={handleSubmit}
              startIcon={<FacebookIcon />}
              sx={{ bgcolor: FB_BLUE_INK, "&:hover": { bgcolor: "#0A4DB3" } }}
            >
              {t("dashboardPage.widget.fbPostingPromo.dialog.submit")}
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * The popup's call to action (`<fbpostingbeta>`): the sign-up dialog, stacked over the popup so
 * closing it returns the realtor to the update. An account with the feature gets a LINK to the
 * page, which the popup counts as "seen" like any link in an update.
 */
export function FbPostingBetaCta(): JSX.Element {
  const { t } = useTranslation();
  const hasFeature = useHasFbPosting();
  const { hasJoined, markJoined } = useFbPostingBetaSignup();
  const [open, setOpen] = useState(false);

  const buttonSx = {
    px: 3,
    py: 1.25,
    fontSize: 15,
    fontWeight: 700,
    borderRadius: "10px",
    color: "#fff",
    background: `linear-gradient(135deg, ${FB_BLUE_INK} 0%, ${FB_BLUE} 100%)`,
    boxShadow: `0 10px 22px -10px ${alpha(FB_BLUE, 0.8)}`,
    "&:hover": { background: `linear-gradient(135deg, #0A4DB3 0%, ${FB_BLUE_INK} 100%)` }
  };

  // A span with `display: block`: Markdown wraps the custom element in a paragraph.
  return (
    <>
      <Box component="span" sx={{ display: "block", textAlign: "center", my: 3 }}>
        {hasFeature ? (
          <Button component={RouterLink} to={PATH_DASHBOARD.general.fbPosting} startIcon={<FacebookIcon />} sx={buttonSx}>
            {t("dashboardPage.widget.fbPostingPromo.ctaOpen")}
          </Button>
        ) : hasJoined ? (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: "10px",
              fontWeight: 700,
              color: "success.dark",
              backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12)
            }}
          >
            <CheckCircleRoundedIcon fontSize="small" />
            {t("dashboardPage.widget.fbPostingPromo.ctaJoined")}
          </Box>
        ) : (
          <Button onClick={() => setOpen(true)} startIcon={<FacebookIcon />} sx={buttonSx}>
            {t("dashboardPage.widget.fbPostingPromo.cta")}
          </Button>
        )}
      </Box>
      <FbPostingBetaDialog
        open={open}
        source="feature-update-popup"
        onClose={() => setOpen(false)}
        onJoined={markJoined}
      />
    </>
  );
}

// ---- the banner ------------------------------------------------------------------------------

/** One capability in the banner's feature row. */
function CapabilityChip({ icon, label }: { icon: JSX.Element; label: string }): JSX.Element {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      sx={{
        px: 1.25,
        py: 0.6,
        borderRadius: 99,
        color: "#fff",
        backgroundColor: alpha("#fff", 0.12),
        border: `1px solid ${alpha("#fff", 0.18)}`,
        "& svg": { fontSize: 16, opacity: 0.95 }
      }}
    >
      {icon}
      <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {label}
      </Typography>
    </Stack>
  );
}

export function FacebookPostingPromoBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { isReady, isDismissed, dismiss } = usePromoBanner(PROMO_BANNER_IDS.facebookGroupPosting);
  const hasFeature = useHasFbPosting();
  const { hasJoined, markJoined } = useFbPostingBetaSignup();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!PROMO_ENABLED) return null;

  // Dismissal lives on the user doc, so it isn't known synchronously: render nothing until the doc
  // lands rather than flash a banner the user already closed.
  if (!isReady) return null;

  // Stay mounted while the dialog is open so its thank-you can render.
  if (isDismissed && !dialogOpen) return null;

  const ctaSx = {
    whiteSpace: "nowrap",
    px: 2.75,
    backgroundColor: "#fff",
    color: FB_BLUE_INK,
    fontWeight: 700,
    boxShadow: "0 10px 24px -10px rgba(3, 10, 48, 0.7)",
    "&:hover": { backgroundColor: alpha("#fff", 0.92) }
  };

  return (
    <>
      {!isDismissed && (
        <RootStyle>
          <Tooltip title={t("general.close") as string}>
            <IconButton
              size="small"
              onClick={dismiss}
              aria-label={t("general.close") as string}
              sx={{
                position: "absolute",
                top: 8,
                insetInlineEnd: 8,
                zIndex: 4,
                color: alpha("#fff", 0.9),
                "&:hover": { backgroundColor: alpha("#fff", 0.15) }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <CardContent
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              gap: { xs: 2, md: 4 },
              py: { xs: 3, md: 3.5 },
              px: { xs: 2.5, md: 4 },
              "&:last-child": { pb: { xs: 3, md: 3.5 } }
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "start", paddingInlineEnd: { xs: 3, md: 0 } }}>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap" }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fff",
                    boxShadow: "0 6px 16px -6px rgba(3, 10, 48, 0.6)"
                  }}
                >
                  <FacebookIcon sx={{ fontSize: 28, color: FB_BLUE }} />
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.85,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 99,
                    fontSize: 12,
                    fontWeight: 700,
                    backgroundColor: alpha("#fff", 0.14),
                    border: `1px solid ${alpha("#fff", 0.28)}`
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#4ADE80",
                      animation: `${pulse} 2s ease-out infinite`,
                      "@media (prefers-reduced-motion: reduce)": { animation: "none" }
                    }}
                  />
                  {t("dashboardPage.widget.fbPostingPromo.badgeBeta")}
                </Box>
              </Stack>

              <Typography
                variant="h3"
                component="h2"
                sx={{ mb: 1.25, color: "#fff", lineHeight: 1.15, fontSize: { xs: 26, md: 34 } }}
              >
                {t("dashboardPage.widget.fbPostingPromo.titleLead")}{" "}
                <Box
                  component="span"
                  sx={{
                    backgroundImage: "linear-gradient(90deg, #A5F3FC 0%, #C4B5FD 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent"
                  }}
                >
                  {t("dashboardPage.widget.fbPostingPromo.titleAccent")}
                </Box>
              </Typography>
              <Typography variant="body1" sx={{ color: alpha("#fff", 0.92), maxWidth: 620, lineHeight: 1.6 }}>
                {t("dashboardPage.widget.fbPostingPromo.description")}
              </Typography>

              <Stack direction="row" gap={1} sx={{ mt: 2, flexWrap: "wrap" }}>
                <CapabilityChip icon={<AutoAwesomeIcon />} label={t("dashboardPage.widget.fbPostingPromo.capabilityAi")} />
                <CapabilityChip icon={<GroupsIcon />} label={t("dashboardPage.widget.fbPostingPromo.capabilityGroups")} />
                <CapabilityChip icon={<ScheduleIcon />} label={t("dashboardPage.widget.fbPostingPromo.capabilitySchedule")} />
                <CapabilityChip icon={<InsightsIcon />} label={t("dashboardPage.widget.fbPostingPromo.capabilityTracking")} />
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={{ xs: 1.25, sm: 2 }}
                sx={{ mt: 2.75 }}
              >
                {hasFeature ? (
                  <Button
                    component={RouterLink}
                    to={PATH_DASHBOARD.general.fbPosting}
                    variant="contained"
                    size="large"
                    startIcon={<FacebookIcon />}
                    sx={ctaSx}
                  >
                    {t("dashboardPage.widget.fbPostingPromo.ctaOpen")}
                  </Button>
                ) : hasJoined ? (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      px: 2.25,
                      py: 1.1,
                      borderRadius: 1,
                      fontWeight: 700,
                      backgroundColor: alpha("#fff", 0.16),
                      border: `1px solid ${alpha("#fff", 0.3)}`
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ fontSize: 20, color: "#4ADE80" }} />
                    {t("dashboardPage.widget.fbPostingPromo.ctaJoined")}
                  </Box>
                ) : (
                  <Button
                    onClick={() => setDialogOpen(true)}
                    variant="contained"
                    size="large"
                    startIcon={<FacebookIcon />}
                    sx={ctaSx}
                  >
                    {t("dashboardPage.widget.fbPostingPromo.cta")}
                  </Button>
                )}
                <Typography variant="body2" sx={{ color: alpha("#fff", 0.9), fontWeight: 600 }}>
                  {hasFeature
                    ? t("dashboardPage.widget.fbPostingPromo.sublineOpen")
                    : hasJoined
                    ? t("dashboardPage.widget.fbPostingPromo.sublineJoined")
                    : t("dashboardPage.widget.fbPostingPromo.subline")}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                component="p"
                sx={{ mt: 1.75, fontSize: 11, color: alpha("#fff", 0.66) }}
              >
                {t("dashboardPage.widget.fbPostingPromo.paidNote")}
              </Typography>
            </Box>

            <Box sx={{ display: { xs: "none", md: "block" }, marginInlineEnd: 3, mt: 1 }}>
              <FbPostingPromoArt />
            </Box>
          </CardContent>
        </RootStyle>
      )}

      <FbPostingBetaDialog
        open={dialogOpen}
        source="dashboard-promo-banner"
        onClose={() => setDialogOpen(false)}
        onJoined={markJoined}
      />
    </>
  );
}

export default FacebookPostingPromoBanner;
